import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Bug, RefreshCw, Eye, Trash2, CheckCircle2, AlertTriangle, Filter, Camera, ExternalLink, MessageSquare, X, Download, TrendingUp } from 'lucide-react';

const STATUS_META = {
  new:          { label: 'New',         color: '#D4726A', bg: '#FEF2F2' },
  triaging:     { label: 'Triaging',    color: '#BA7517', bg: '#FEF3C7' },
  in_progress:  { label: 'In Progress', color: '#3B5BA9', bg: '#EEF2FF' },
  fixed:        { label: 'Fixed',       color: '#2D8B5E', bg: '#DCFCE7' },
  wont_fix:     { label: "Won't Fix",   color: '#6B7280', bg: '#F3F4F6' },
  duplicate:    { label: 'Duplicate',   color: '#6B7280', bg: '#F3F4F6' },
};

const SEVERITY_META = {
  low:      { label: 'Low',      color: '#6B7280' },
  medium:   { label: 'Medium',   color: '#BA7517' },
  high:     { label: 'High',     color: '#D4726A' },
  critical: { label: 'Critical', color: '#BA3F3F' },
};

const CATEGORY_LABELS = {
  bug: '🐛 Bug', data: '📊 Data', ux: '🎨 UX', performance: '⚡ Perf',
  feature_request: '💡 Feature', question: '❓ Question', other: '🗒 Other',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

export default function IssueInbox() {
  const [data, setData] = useState({ issues: [], counts: {}, total: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [shotUrl, setShotUrl] = useState(null);
  const [busy, setBusy] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [res, sum] = await Promise.all([
        api.get(`/issues${filter !== 'all' ? `?status=${filter}` : ''}`),
        api.get('/issues/reports/summary'),
      ]);
      setData(res.data);
      setSummary(sum.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]);

  const openDetail = async (issue) => {
    setSelected(issue);
    setShotUrl(null);
    if (issue.screenshot_size || issue.has_screenshot) {
      // lazy-fetch screenshot via authenticated endpoint
      const baseURL = api.defaults.baseURL;
      setShotUrl(`${baseURL}/issues/${issue.id}/screenshot`);
    }
    try {
      const r = await api.get(`/issues/${issue.id}`);
      setSelected(s => ({ ...(s || {}), ...r.data }));
      if (r.data.screenshot_size) {
        const baseURL = api.defaults.baseURL;
        setShotUrl(`${baseURL}/issues/${r.data.id}/screenshot`);
      }
    } catch { /* silent */ }
  };

  const changeStatus = async (status) => {
    if (!selected) return;
    setBusy('status');
    try {
      const r = await api.patch(`/issues/${selected.id}`, { status, admin_note: selected.admin_note || '' });
      setSelected(r.data);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(''); }
  };

  const saveNote = async () => {
    if (!selected) return;
    setBusy('note');
    try {
      const r = await api.patch(`/issues/${selected.id}`, {
        status: selected.status,
        admin_note: selected.admin_note || '',
      });
      setSelected(r.data);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Update failed');
    } finally { setBusy(''); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this issue permanently?')) return;
    setBusy(id);
    try {
      await api.delete(`/issues/${id}`);
      if (selected?.id === id) setSelected(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(''); }
  };

  const counts = data.counts || {};
  const filterPills = [
    { v: 'all',         label: 'All',         n: data.total || 0 },
    { v: 'new',         label: 'New',         n: counts.new || 0 },
    { v: 'triaging',    label: 'Triaging',    n: counts.triaging || 0 },
    { v: 'in_progress', label: 'In Progress', n: counts.in_progress || 0 },
    { v: 'fixed',       label: 'Fixed',       n: counts.fixed || 0 },
  ];

  return (
    <div className="space-y-4" data-testid="issue-inbox">
      {/* UAT Friday Summary — the 30-second read */}
      {summary && summary.headline.total > 0 && (
        <div className="rounded-xl border p-5" style={{ borderColor: '#1B2A4A', background: 'linear-gradient(135deg, #1B2A4A 0%, #2A7B88 100%)' }} data-testid="uat-summary-card">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp size={16} style={{ color: '#F5C86B' }} />
              <h3 className="text-sm font-bold" style={{ color: '#fff' }}>UAT Summary — Monday Decision Card</h3>
            </div>
            <button onClick={() => {
              const blob = new Blob([JSON.stringify(summary, null, 2)], { type: 'application/json' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `uat_summary_${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(a.href);
            }} className="text-[11px] flex items-center gap-1" style={{ color: '#F5C86B' }} data-testid="uat-summary-download">
              <Download size={11} /> Download
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className="rounded p-2.5" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: '#E5E1DC' }}>Total filed</p>
              <p className="text-lg font-bold" style={{ color: '#fff' }}>{summary.headline.total}</p>
            </div>
            <div className="rounded p-2.5" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: '#E5E1DC' }}>Still open</p>
              <p className="text-lg font-bold" style={{ color: summary.headline.open > 0 ? '#F5C86B' : '#fff' }}>{summary.headline.open}</p>
            </div>
            <div className="rounded p-2.5" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: '#E5E1DC' }}>Critical open</p>
              <p className="text-lg font-bold" style={{ color: summary.headline.critical_open > 0 ? '#FCA5A5' : '#fff' }}>{summary.headline.critical_open}</p>
            </div>
            <div className="rounded p-2.5" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: '#E5E1DC' }}>Avg time-to-fix</p>
              <p className="text-lg font-bold" style={{ color: '#fff' }}>{summary.avg_hours_to_fix != null ? `${summary.avg_hours_to_fix}h` : '—'}</p>
            </div>
          </div>
          <div className="rounded p-2.5 mb-3" style={{ backgroundColor: 'rgba(245,200,107,0.15)' }}>
            <p className="text-[11px] font-semibold" style={{ color: '#F5C86B' }}>💡 {summary.headline.decision_hint}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
            <div>
              <p className="font-semibold mb-1" style={{ color: '#F5C86B' }}>Top reporters</p>
              {summary.top_reporters.slice(0, 3).map((r, i) => (
                <p key={i} style={{ color: '#E5E1DC' }}>{r.name || r.email} <span className="opacity-70">({r.count})</span></p>
              ))}
              {summary.top_reporters.length === 0 && <p style={{ color: '#9CA3AF' }}>—</p>}
            </div>
            <div>
              <p className="font-semibold mb-1" style={{ color: '#F5C86B' }}>Most-reported pages</p>
              {summary.top_pages.slice(0, 3).map((p, i) => (
                <p key={i} style={{ color: '#E5E1DC' }} className="truncate">{p.path} <span className="opacity-70">({p.count})</span></p>
              ))}
              {summary.top_pages.length === 0 && <p style={{ color: '#9CA3AF' }}>—</p>}
            </div>
            <div>
              <p className="font-semibold mb-1" style={{ color: '#F5C86B' }}>Oldest still-open</p>
              {summary.oldest_open.slice(0, 3).map((o, i) => (
                <p key={i} style={{ color: '#E5E1DC' }} className="truncate">{o.title} <span className="opacity-70">({o.days_open}d)</span></p>
              ))}
              {summary.oldest_open.length === 0 && <p style={{ color: '#9CA3AF' }}>—</p>}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-xl border p-5" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-2">
            <Bug size={18} style={{ color: '#D4726A' }} />
            <div>
              <h2 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Issue Reports</h2>
              <p className="text-[11px]" style={{ color: '#6B7280' }}>
                UAT user feedback — every click on the red "Report an issue" button lands here.
              </p>
            </div>
          </div>
          <button onClick={load} className="px-2.5 py-1.5 rounded-md border text-[11px] flex items-center gap-1" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="issue-refresh">
            <RefreshCw size={11} /> Refresh
          </button>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          <Filter size={12} style={{ color: '#9CA3AF' }} />
          {filterPills.map(p => {
            const active = filter === p.v;
            return (
              <button key={p.v} onClick={() => setFilter(p.v)}
                className="px-2.5 py-1 rounded-full text-[11px] font-medium border"
                style={{
                  backgroundColor: active ? '#1B2A4A' : '#fff',
                  color: active ? '#fff' : '#6B7280',
                  borderColor: active ? '#1B2A4A' : '#E5E1DC',
                }}
                data-testid={`issue-filter-${p.v}`}>
                {p.label} <span className="ml-1 opacity-70">{p.n}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data.issues.length === 0 ? (
          <div className="py-12 text-center text-xs" style={{ color: '#9CA3AF' }}>
            No issues in this filter. {filter === 'new' ? '🎉 Inbox zero!' : ''}
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: '#F3F4F6' }}>
            {data.issues.map(i => {
              const sm = STATUS_META[i.status] || STATUS_META.new;
              const sev = SEVERITY_META[i.severity] || SEVERITY_META.medium;
              return (
                <div key={i.id} className="px-4 py-3 hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(i)} data-testid={`issue-row-${i.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: sm.bg, color: sm.color }}>
                          {sm.label}
                        </span>
                        <span className="text-[10px] font-semibold" style={{ color: sev.color }}>
                          {sev.label}
                        </span>
                        <span className="text-[10px]" style={{ color: '#6B7280' }}>
                          {CATEGORY_LABELS[i.category] || i.category}
                        </span>
                      </div>
                      <p className="text-sm font-semibold truncate" style={{ color: '#1B2A4A' }}>{i.title}</p>
                      <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>
                        {i.reported_by_name || i.reported_by} · {fmtDate(i.created_at)}
                      </p>
                    </div>
                    <Eye size={13} style={{ color: '#9CA3AF' }} className="mt-1 shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-start justify-end" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={() => setSelected(null)} data-testid="issue-detail-drawer">
          <div className="bg-white h-full w-full sm:max-w-xl shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between" style={{ borderColor: '#F3F4F6' }}>
              <p className="text-sm font-bold truncate pr-4" style={{ color: '#1B2A4A' }}>{selected.title}</p>
              <button onClick={() => setSelected(null)} className="p-1 rounded hover:bg-gray-100" data-testid="issue-detail-close"><X size={16} style={{ color: '#6B7280' }} /></button>
            </div>

            <div className="p-5 space-y-4">
              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold px-2 py-0.5 rounded"
                  style={{ backgroundColor: (STATUS_META[selected.status] || STATUS_META.new).bg, color: (STATUS_META[selected.status] || STATUS_META.new).color }}>
                  {(STATUS_META[selected.status] || STATUS_META.new).label}
                </span>
                <span className="text-[10px] font-semibold" style={{ color: (SEVERITY_META[selected.severity] || SEVERITY_META.medium).color }}>
                  {(SEVERITY_META[selected.severity] || SEVERITY_META.medium).label} severity
                </span>
                <span className="text-[10px]" style={{ color: '#6B7280' }}>
                  {CATEGORY_LABELS[selected.category] || selected.category}
                </span>
              </div>

              {/* Meta */}
              <div className="rounded-md p-3 text-[11px] space-y-1" style={{ backgroundColor: '#F9FAFB', color: '#374151' }}>
                <p><b>From:</b> {selected.reported_by_name} ({selected.reported_by}) · {selected.reported_by_role}{selected.reported_by_division ? ` · ${selected.reported_by_division}` : ''}</p>
                <p><b>Reported:</b> {fmtDate(selected.created_at)}</p>
                {selected.page_url && (
                  <p><b>On page:</b> <a href={selected.page_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1" style={{ color: '#2A7B88' }}>{selected.page_url} <ExternalLink size={10} /></a></p>
                )}
                {selected.user_agent && <p className="opacity-70"><b>Browser:</b> {selected.user_agent.slice(0, 90)}…</p>}
                {selected.resolved_at && <p><b>Resolved:</b> {fmtDate(selected.resolved_at)}</p>}
              </div>

              {/* Description */}
              {selected.description && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: '#9CA3AF' }}>Details</p>
                  <p className="text-sm whitespace-pre-wrap" style={{ color: '#1B2A4A' }}>{selected.description}</p>
                </div>
              )}

              {/* Screenshot */}
              {(selected.screenshot_size || shotUrl) && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold mb-1 flex items-center gap-1" style={{ color: '#9CA3AF' }}>
                    <Camera size={11} /> Screenshot
                  </p>
                  {shotUrl && <img src={shotUrl} alt="screenshot" className="rounded-md border w-full" style={{ borderColor: '#E5E1DC' }} data-testid="issue-screenshot-img" />}
                </div>
              )}

              {/* Status changer */}
              <div>
                <p className="text-[10px] uppercase tracking-wider font-semibold mb-1.5" style={{ color: '#9CA3AF' }}>Change status</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(STATUS_META).map(([k, v]) => (
                    <button key={k} onClick={() => changeStatus(k)}
                      disabled={busy === 'status' || selected.status === k}
                      className="px-2.5 py-1 rounded text-[11px] font-medium border disabled:opacity-60"
                      style={{
                        borderColor: selected.status === k ? v.color : '#E5E1DC',
                        backgroundColor: selected.status === k ? v.bg : '#fff',
                        color: selected.status === k ? v.color : '#6B7280',
                      }}
                      data-testid={`issue-status-${k}`}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Admin note */}
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1" style={{ color: '#9CA3AF' }}>
                  <MessageSquare size={10} /> Internal note
                </label>
                <textarea value={selected.admin_note || ''}
                  onChange={e => setSelected(s => ({ ...s, admin_note: e.target.value }))}
                  rows={3}
                  placeholder="Notes only visible to you (e.g., who's working on it, ETA, root cause)"
                  className="mt-1 w-full text-xs border rounded px-2 py-1.5 resize-none" style={{ borderColor: '#E5E1DC' }}
                  data-testid="issue-admin-note" />
                <div className="flex items-center justify-end gap-2 mt-2">
                  <button onClick={() => remove(selected.id)} disabled={busy === selected.id}
                    className="px-2.5 py-1.5 rounded border text-[11px] flex items-center gap-1 disabled:opacity-60"
                    style={{ borderColor: '#FECACA', color: '#D4726A' }}
                    data-testid="issue-delete">
                    <Trash2 size={11} /> Delete
                  </button>
                  <button onClick={saveNote} disabled={busy === 'note'}
                    className="px-3 py-1.5 rounded text-[11px] font-semibold text-white disabled:opacity-60"
                    style={{ backgroundColor: '#2A7B88' }}
                    data-testid="issue-save-note">
                    {busy === 'note' ? 'Saving…' : 'Save note'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
